import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { theme } from '../theme';
import { downloadOdcBusinessReport, parseReportData } from '../utils/odcReportDownload';
import { useCopyContext } from '../context/CopyContext';

type ReportStatus = 'loading' | 'generating' | 'completed' | 'failed';
type SectionKey = 'overview' | 'agents' | 'content' | 'roadmap' | 'roi';

function buildProfileFromBusiness(b: Record<string, unknown>) {
  return {
    businessName: String(b.business_name || ''),
    businessType: String(b.business_type || 'Other'),
    location: String(b.location || b.city || ''),
    targetAudience: String(b.target_audience || ''),
    monthlyBudget: 'medium',
    primaryGoal: 'leads',
    socialPlatforms: ['Instagram', 'Facebook', 'WhatsApp', 'YouTube', 'LinkedIn'],
    teamSize: 'small',
    businessAge: 'established',
    description: String(b.description || ''),
  };
}

function handleShareReport(copyText: (text: string, message?: string) => void) {
  const reportUrl = window.location.href;
  if (navigator.share) {
    void navigator.share({
      title: 'My ODC Business Report',
      text: 'Check out my AI-generated business marketing report from ODC!',
      url: reportUrl,
    });
  } else {
    copyText(reportUrl, 'Report link copied!')
  }
}

const DAY_KEYS = ['monday', 'wednesday', 'friday', 'sunday'] as const;
const DAY_LABELS: Record<(typeof DAY_KEYS)[number], string> = {
  monday: 'Mon',
  wednesday: 'Wed',
  friday: 'Fri',
  sunday: 'Sun',
};

export function OnboardingReportPage() {
  const { copyText } = useCopyContext();
  const nav = useNavigate();
  const [reportStatus, setReportStatus] = useState<ReportStatus>('loading');
  const [report, setReport] = useState<Record<string, unknown> | null>(null);
  const [activeSection, setActiveSection] = useState<SectionKey>('overview');
  const [activePhase, setActivePhase] = useState(0);
  const [genUiStep, setGenUiStep] = useState(0);
  const pollInterval = useRef<number | null>(null);
  const [expandedCaption, setExpandedCaption] = useState<string | null>(null);

  const clearPoll = useCallback(() => {
    if (pollInterval.current) {
      clearInterval(pollInterval.current);
      pollInterval.current = null;
    }
  }, []);

  const pollForReport = useCallback(
    (id: string) => {
      clearPoll();
      setReportStatus('generating');
      pollInterval.current = window.setInterval(async () => {
        try {
          const r = await api.get<{ status: string }>(`/reports/${id}/status`);
          if (r.data.status === 'completed') {
            clearPoll();
            const full = await api.get<{ report: Record<string, unknown> }>(`/reports/${id}`);
            const row = full.data.report;
            const parsed = parseReportData(row.report_data);
            if (!parsed) {
              setReportStatus('failed');
              return;
            }
            setReport(parsed);
            setReportStatus('completed');
            localStorage.removeItem('odc_report_id');
          } else if (r.data.status === 'failed') {
            clearPoll();
            setReportStatus('failed');
          }
        } catch {
          clearPoll();
          setReportStatus('failed');
        }
      }, 3000);
    },
    [clearPoll]
  );

  useEffect(() => {
    return () => clearPoll();
  }, [clearPoll]);

  useEffect(() => {
    if (reportStatus !== 'generating') return;
    const t = window.setInterval(() => {
      setGenUiStep((s) => Math.min(5, s + 1));
    }, 5000);
    return () => window.clearInterval(t);
  }, [reportStatus]);

  useEffect(() => {
    const reportId = localStorage.getItem('odc_report_id');
    if (!reportId) {
      void api
        .get<{ report: Record<string, unknown> | null }>('/reports/latest')
        .then((r) => {
          const row = r.data.report;
          if (!row) {
            setReportStatus('failed');
            return;
          }
          const st = String(row.status || '');
          if (st === 'completed') {
            const parsed = parseReportData(row.report_data);
            if (!parsed) {
              setReportStatus('failed');
              return;
            }
            setReport(parsed);
            setReportStatus('completed');
            return;
          }
          if (st === 'failed') {
            setReportStatus('failed');
            return;
          }
          pollForReport(String(row.id));
        })
        .catch(() => setReportStatus('failed'));
      return;
    }
    setReportStatus('generating');
    pollForReport(reportId);
  }, [pollForReport]);

  async function tryGenerateAgain() {
    try {
      const { data } = await api.get<{ business: Record<string, unknown> | null }>('/business/me');
      if (!data.business) {
        setReportStatus('failed');
        return;
      }
      const profile = buildProfileFromBusiness(data.business);
      const res = await api.post<{ report_id: string }>('/reports/generate', profile);
      localStorage.setItem('odc_report_id', res.data.report_id);
      setGenUiStep(0);
      pollForReport(res.data.report_id);
    } catch {
      setReportStatus('failed');
    }
  }

  if (reportStatus === 'generating') {
    return (
      <div className="odc-page relative z-10 flex min-h-[70vh] flex-col items-center justify-center px-4 py-16 text-center">
        <style>{`
          @keyframes odc-bounce-robot {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }
          .odc-report-bounce {
            animation: odc-bounce-robot 1s ease-in-out infinite;
          }
          @keyframes odc-bar-fill {
            from { width: 0%; }
            to { width: 95%; }
          }
          .odc-gen-bar-inner {
            animation: odc-bar-fill 55s ease-out forwards;
          }
        `}</style>
        <div className="odc-report-bounce text-7xl">🤖</div>
        <h2 className="mt-6 text-2xl font-black md:text-3xl" style={{ color: 'var(--text)' }}>
          ODC AI is building your report...
        </h2>
        <p className="mt-3 max-w-md text-[15px]" style={{ color: 'var(--muted)' }}>
          This may take 30-60 seconds. Please wait.
        </p>
        <div className="mt-10 h-2 w-full max-w-md overflow-hidden rounded-full" style={{ background: 'var(--card2)' }}>
          <div className="odc-gen-bar-inner h-full rounded-full" style={{ background: theme.primary }} />
        </div>
        <ul className="mx-auto mt-10 max-w-md space-y-3 text-left text-sm" style={{ color: 'var(--muted)' }}>
          {[
            'Business profile analyzed',
            'AI agents evaluated',
            'Industry research completed',
            'Creating your content strategy...',
            'Preparing agent recommendations...',
            'Calculating ROI projections...',
          ].map((label, i) => (
            <li
              key={label}
              className="flex gap-2 transition-opacity duration-500"
              style={{ opacity: genUiStep >= i ? 1 : 0.25 }}
            >
              <span>{genUiStep > i ? '✅' : genUiStep === i ? '⏳' : '○'}</span>
              {label}
            </li>
          ))}
        </ul>
        <button
          type="button"
          className="odc-btn-ghost mt-14 text-sm"
          onClick={() => nav('/dashboard')}
        >
          Go to Dashboard →
        </button>
      </div>
    );
  }

  if (reportStatus === 'failed') {
    return (
      <div className="odc-page relative z-10 mx-auto max-w-lg px-4 py-20 text-center">
        <p className="text-4xl">⚠️</p>
        <h2 className="mt-4 text-xl font-bold" style={{ color: 'var(--text)' }}>
          Report generation failed
        </h2>
        <p className="mt-3 text-sm" style={{ color: 'var(--muted)' }}>
          You can return to the Dashboard and try again from the Reports section.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button type="button" className="odc-btn-primary" onClick={() => nav('/dashboard')}>
            Go to Dashboard
          </button>
          <button type="button" className="odc-btn-ghost" onClick={() => void tryGenerateAgain()}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (reportStatus === 'loading' || !report) {
    return (
      <div className="odc-page relative z-10 flex min-h-[40vh] items-center justify-center px-4 py-16">
        <p style={{ color: 'var(--muted)' }}>Loading…</p>
      </div>
    );
  }

  const es = report.executive_summary as Record<string, string> | undefined;
  const ca = report.competitive_analysis as Record<string, unknown> | undefined;
  const qw = (report.quick_wins as Array<Record<string, string>> | undefined) ?? [];
  const pr = report.platform_recommendations as Record<string, string[]> | undefined;
  const ar = report.agent_recommendations as Record<string, unknown> | undefined;
  const cs = report.content_strategy as Record<string, unknown> | undefined;
  const phases = (cs?.phases as Array<Record<string, unknown>> | undefined) ?? [];
  const phase = phases[activePhase] ?? phases[0];
  const gr = report.growth_roadmap as Record<string, Record<string, unknown>> | undefined;
  const roi = report.roi_projection as Record<string, unknown> | undefined;
  const plan = report.recommended_plan as Record<string, unknown> | undefined;

  const essentialAgents = (ar?.essential as Array<Record<string, unknown>> | undefined) ?? [];
  const recommendedAgents = (ar?.recommended as Array<Record<string, unknown>> | undefined) ?? [];
  const notNeeded = (ar?.not_needed as string[] | undefined) ?? [];

  const tabs: { key: SectionKey; label: string }[] = [
    { key: 'overview', label: '📊 Overview' },
    { key: 'agents', label: '🤖 Agents' },
    { key: 'content', label: '📅 Content' },
    { key: 'roadmap', label: '🗺️ Roadmap' },
    { key: 'roi', label: '💰 ROI' },
  ];

  return (
    <div className="odc-page relative z-10 space-y-8 pb-16">
      <section
        className="odc-card overflow-hidden !p-0"
        style={{
          borderColor: 'rgba(16,185,129,0.35)',
          background: 'linear-gradient(135deg, rgba(16,185,129,0.14), var(--card))',
        }}
      >
        <div className="flex flex-col gap-6 p-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold" style={{ color: theme.primary }}>
              🎉 Your ODC Business Report is Ready!
            </p>
            <h1 className="mt-2 text-2xl font-black md:text-3xl" style={{ color: 'var(--text)' }}>
              {es?.headline || 'Your personalized marketing intelligence'}
            </h1>
            <p className="mt-3 text-sm leading-relaxed" style={{ color: 'var(--muted)' }}>
              {es?.overview}
            </p>
          </div>
          <div className="flex shrink-0 flex-col gap-2 sm:flex-row lg:flex-col">
            <button type="button" className="odc-btn-ghost !text-sm" onClick={() => downloadOdcBusinessReport(report)}>
              📥 Download Report
            </button>
            <button type="button" className="odc-btn-ghost !text-sm" onClick={() => handleShareReport(copyText)}>
              📤 Share with Client
            </button>
            <Link to="/dashboard" className="odc-btn-primary !text-center !text-sm">
              🚀 Go to Dashboard
            </Link>
          </div>
        </div>
        <div className="grid gap-3 border-t px-6 py-4 sm:grid-cols-2 lg:grid-cols-4" style={{ borderColor: 'var(--border)' }}>
          {[
            'Agent recommendations ready',
            'Content calendar ready',
            '3 Month Plan Ready',
            `${String(plan?.plan_name || 'Growth')} Plan Suggested`,
          ].map((t) => (
            <div
              key={t}
              className="rounded-xl px-3 py-2 text-center text-xs font-bold"
              style={{ background: 'var(--card2)', color: 'var(--text)' }}
            >
              {t}
            </div>
          ))}
        </div>
      </section>

      <div
        className="odc-card sticky top-0 z-20 !flex !flex-wrap gap-2 !p-3"
        style={{ transform: 'none', backdropFilter: 'blur(8px)' }}
      >
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`odc-pill-tab ${activeSection === t.key ? 'active' : ''}`}
            style={
              activeSection === t.key
                ? { borderColor: theme.primary, background: `${theme.primary}22`, color: 'var(--text)' }
                : undefined
            }
            onClick={() => setActiveSection(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeSection === 'overview' ? (
        <div className="space-y-6">
          <div className="odc-card !p-6">
            <h2 className="text-lg font-bold" style={{ color: 'var(--text)' }}>
              📊 Business Analysis
            </h2>
            <div className="mt-4 grid gap-6 md:grid-cols-2">
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                    Market Opportunity
                  </p>
                  <p className="mt-1 text-sm leading-relaxed" style={{ color: 'var(--text)' }}>
                    {es?.market_opportunity}
                  </p>
                </div>
                <div
                  className="rounded-xl border p-4"
                  style={{ borderColor: 'rgba(16,185,129,0.45)', background: 'rgba(16,185,129,0.08)' }}
                >
                  <p className="text-xs font-bold" style={{ color: theme.primary }}>
                    💡 Key Insight
                  </p>
                  <p className="mt-2 text-sm" style={{ color: 'var(--text)' }}>
                    {es?.key_insight}
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                    Competitive Position
                  </p>
                  <p className="mt-1 text-sm leading-relaxed" style={{ color: 'var(--text)' }}>
                    {String(ca?.market_position || '')}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                    Opportunities
                  </p>
                  <ul className="mt-2 space-y-1 text-sm" style={{ color: 'var(--text)' }}>
                    {((ca?.opportunities as string[]) ?? []).map((o) => (
                      <li key={o}>✅ {o}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="odc-card !p-6">
            <h2 className="text-lg font-bold" style={{ color: 'var(--text)' }}>
              ⚡ Quick Wins — Start This Week
            </h2>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              {qw.map((x) => (
                <div key={x.action} className="odc-card !rounded-xl !p-4" style={{ transform: 'none' }}>
                  <span
                    className="inline-block rounded-md px-2 py-0.5 text-[10px] font-bold"
                    style={{ background: 'rgba(245,158,11,0.2)', color: '#FBBF24' }}
                  >
                    {x.timeline}
                  </span>
                  <p className="mt-2 font-bold" style={{ color: 'var(--text)' }}>
                    {x.action}
                  </p>
                  <p className="mt-1 text-[13px]" style={{ color: 'var(--muted)' }}>
                    {x.expected_result}
                  </p>
                  <span
                    className="mt-2 inline-block rounded-md px-2 py-0.5 text-[11px] font-bold"
                    style={{ background: 'rgba(16,185,129,0.2)', color: '#6EE7B7' }}
                  >
                    Agent: {x.agent}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="odc-card !p-6">
            <h2 className="text-lg font-bold" style={{ color: 'var(--text)' }}>
              📱 Platform Strategy
            </h2>
            <div className="mt-4 space-y-3 text-sm">
              <p>
                <span className="font-bold" style={{ color: theme.primary }}>
                  Primary:{' '}
                </span>
                {(pr?.primary ?? []).join(', ')}
              </p>
              <p>
                <span className="font-bold" style={{ color: '#22D3EE' }}>
                  Secondary:{' '}
                </span>
                {(pr?.secondary ?? []).join(', ')}
              </p>
              <p style={{ color: 'var(--muted)' }}>
                <span className="font-bold">Skip for now: </span>
                {(pr?.skip_for_now ?? []).join(', ')}
              </p>
              <p className="text-sm italic" style={{ color: 'var(--muted)' }}>
                {pr?.reasoning}
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {activeSection === 'agents' ? (
        <div className="space-y-6">
          <div className="odc-card !p-6">
            <h2 className="text-lg font-bold" style={{ color: 'var(--text)' }}>
              🤖 AI Agents Recommended for Your Business
            </h2>
            <div className="mt-3 flex flex-wrap gap-2 text-xs font-bold">
              <span className="rounded-md px-2 py-1" style={{ background: 'rgba(239,68,68,0.15)', color: '#FCA5A5' }}>
                Essential: {essentialAgents.length}
              </span>
              <span className="rounded-md px-2 py-1" style={{ background: 'rgba(245,158,11,0.15)', color: '#FCD34D' }}>
                Recommended: {recommendedAgents.length}
              </span>
              <span className="rounded-md px-2 py-1" style={{ background: 'var(--card2)', color: 'var(--muted)' }}>
                Optional: {((ar?.optional as unknown[]) ?? []).length}
              </span>
            </div>
            <p className="mt-3 text-sm" style={{ color: 'var(--muted)' }}>
              {String(ar?.recommendation_summary || '')}
            </p>
          </div>

          <div className="odc-card !p-6">
            <h3 className="text-base font-bold" style={{ color: 'var(--text)' }}>
              🔴 Essential Agents — Activate Immediately
            </h3>
            <p className="text-xs" style={{ color: 'var(--muted)' }}>
              These agents are the most critical for your business.
            </p>
            <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {essentialAgents.map((a, idx) => (
                <div
                  key={`${String(a.agent_number)}-${idx}`}
                  className="odc-card !rounded-xl !p-4"
                  style={{ transform: 'none', borderColor: 'rgba(239,68,68,0.3)' }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-2xl">{String(a.icon || '🤖')}</span>
                    <span
                      className="rounded px-2 py-0.5 text-[10px] font-black"
                      style={{ background: 'rgba(239,68,68,0.2)', color: '#FCA5A5' }}
                    >
                      Essential · {String(a.priority_score || '')}/10
                    </span>
                  </div>
                  <p className="mt-2 text-[11px]" style={{ color: 'var(--muted)' }}>
                    #
                    {typeof a.agent_number === 'number' && Number.isFinite(a.agent_number)
                      ? String(a.agent_number).padStart(2, '0')
                      : String(a.agent_number ?? '')}
                  </p>
                  <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>
                    {String(a.name)}
                  </p>
                  <span
                    className="mt-1 inline-block rounded px-2 py-0.5 text-[10px] font-bold"
                    style={{ background: 'var(--card2)', color: theme.accent }}
                  >
                    {String(a.category)}
                  </span>
                  <p className="mt-2 text-[13px]" style={{ color: 'var(--muted)' }}>
                    <span className="font-semibold" style={{ color: 'var(--text)' }}>
                      Why needed:{' '}
                    </span>
                    {String(a.why_needed)}
                  </p>
                  <p className="mt-2 text-[12px] font-semibold" style={{ color: theme.primary }}>
                    Expected impact: {String(a.expected_impact)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="odc-card !p-6">
            <h3 className="text-base font-bold" style={{ color: 'var(--text)' }}>
              🟡 Recommended Agents — Add in Your First Month
            </h3>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {recommendedAgents.map((a, idx) => (
                <div
                  key={`${String(a.name)}-${idx}`}
                  className="odc-card !rounded-xl !p-3 text-sm"
                  style={{ transform: 'none', borderColor: 'rgba(245,158,11,0.25)' }}
                >
                  <p className="font-bold" style={{ color: 'var(--text)' }}>
                    {String(a.icon)} {String(a.name)}
                  </p>
                  <p className="mt-1 text-[12px]" style={{ color: 'var(--muted)' }}>
                    {String(a.why_needed)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="odc-card !p-6" style={{ color: 'var(--muted)' }}>
            <h3 className="text-base font-bold" style={{ color: 'var(--text)' }}>
              ⚫ These Agents Are Not Required Yet
            </h3>
            <p className="mt-2 text-sm">{notNeeded.join(', ')}</p>
          </div>

          <Link to="/agents" className="odc-btn-primary flex w-full justify-center !py-3">
            ⚡ Activate All Essential Agents Now
          </Link>
        </div>
      ) : null}

      {activeSection === 'content' ? (
        <div className="space-y-6">
          <div className="odc-card !p-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <p className="text-xs font-bold uppercase" style={{ color: 'var(--muted)' }}>
                  Brand Voice
                </p>
                <p className="mt-1 text-sm" style={{ color: 'var(--text)' }}>
                  {String(cs?.brand_voice)}
                </p>
                <p className="mt-4 text-xs font-bold uppercase" style={{ color: 'var(--muted)' }}>
                  Content Mix
                </p>
                {Object.entries((cs?.content_mix as Record<string, string>) ?? {}).map(([k, v]) => (
                  <div key={k} className="mt-2">
                    <div className="flex justify-between text-[11px]" style={{ color: 'var(--muted)' }}>
                      <span className="capitalize">{k.replace('_', ' ')}</span>
                      <span>{v}</span>
                    </div>
                    <div className="mt-1 h-2 overflow-hidden rounded-full" style={{ background: 'var(--card2)' }}>
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: v,
                          background: `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div>
                <p className="text-xs font-bold uppercase" style={{ color: 'var(--muted)' }}>
                  Key Themes
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {((cs?.key_themes as string[]) ?? []).map((t) => (
                    <span
                      key={t}
                      className="rounded-full px-3 py-1 text-xs font-bold"
                      style={{ background: 'rgba(16,185,129,0.18)', color: '#6EE7B7' }}
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {phases.map((p, idx) => (
              <button
                key={String(p.phase_number)}
                type="button"
                className={`odc-pill-tab ${activePhase === idx ? 'active' : ''}`}
                style={
                  activePhase === idx
                    ? { borderColor: theme.secondary, background: `${theme.secondary}22` }
                    : undefined
                }
                onClick={() => setActivePhase(idx)}
              >
                Phase {Number(p.phase_number)}: {String(p.duration)}
              </button>
            ))}
          </div>

          {phase ? (
            <div className="odc-card !p-6">
              <h3 className="text-lg font-black" style={{ color: 'var(--text)' }}>
                {String(phase.phase_name)}
              </h3>
              <p className="mt-1 text-sm italic" style={{ color: 'var(--muted)' }}>
                {String(phase.focus)}
              </p>
              <p className="mt-3 text-xs font-bold uppercase" style={{ color: 'var(--muted)' }}>
                Pillars
              </p>
              <p className="text-sm" style={{ color: 'var(--text)' }}>
                {((phase.content_pillars as string[]) ?? []).join(' · ')}
              </p>
              <p className="mt-3 text-xs font-bold uppercase" style={{ color: 'var(--muted)' }}>
                KPIs
              </p>
              <ul className="text-sm" style={{ color: 'var(--text)' }}>
                {((phase.kpis as string[]) ?? []).map((k) => (
                  <li key={k}>
                    • {k}
                  </li>
                ))}
              </ul>

              <div className="mt-8 space-y-6">
                {((phase.weekly_posts as Array<Record<string, unknown>> | undefined) ?? []).map((week) => (
                  <div key={String(week.week)} className="rounded-xl border p-4" style={{ borderColor: 'var(--border)' }}>
                    <p className="text-sm font-bold" style={{ color: theme.accent }}>
                      Week {String(week.week)} — {String(week.theme)}
                    </p>
                    <div className="mt-3 space-y-3">
                      {DAY_KEYS.map((dk) => {
                        const post = week[dk] as Record<string, unknown> | undefined;
                        if (!post) return null;
                        const cap = String(post.caption || post.message || '');
                        const capKey = `${String(week.week)}-${dk}`;
                        const shown = expandedCaption === capKey ? cap : cap.slice(0, 60) + (cap.length > 60 ? '…' : '');
                        const tags = (post.hashtags as string[] | undefined)?.slice(0, 3) ?? [];
                        return (
                          <div
                            key={dk}
                            className="grid gap-2 rounded-lg p-3 text-sm md:grid-cols-[auto,1fr,auto]"
                            style={{ background: 'var(--card2)' }}
                          >
                            <span
                              className="flex h-8 w-10 items-center justify-center rounded-md text-xs font-bold"
                              style={{ background: 'var(--card)', color: theme.primary }}
                            >
                              {DAY_LABELS[dk]}
                            </span>
                            <div className="min-w-0">
                              <p className="text-[11px]" style={{ color: 'var(--muted)' }}>
                                {String(post.platform)} · {String(post.type)}
                              </p>
                              <p className="font-semibold" style={{ color: 'var(--text)' }}>
                                {String(post.topic)}
                              </p>
                              <button
                                type="button"
                                className="mt-1 max-w-full text-left text-[12px]"
                                style={{ color: 'var(--muted)' }}
                                onClick={() =>
                                  setExpandedCaption((c) => (c === capKey ? null : capKey))
                                }
                              >
                                {shown}
                              </button>
                              {tags.length ? (
                                <p className="mt-1 text-[10px]" style={{ color: 'var(--muted)' }}>
                                  {tags.map((h) => `#${h}`).join(' ')}
                                </p>
                              ) : null}
                            </div>
                            <button
                              type="button"
                              className="odc-btn-ghost h-8 self-start !px-2 !py-1 !text-[10px]"
                              onClick={() => copyText(cap, 'Copied!')}
                            >
                              Copy
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 grid gap-3 md:grid-cols-2">
                {Object.entries((phase.platform_strategy as Record<string, string>) ?? {}).map(([plat, strat]) => (
                  <div key={plat} className="odc-card !rounded-xl !p-3 text-sm" style={{ transform: 'none' }}>
                    <p className="font-bold" style={{ color: 'var(--text)' }}>
                      {plat}
                    </p>
                    <p className="mt-1 text-[12px]" style={{ color: 'var(--muted)' }}>
                      {strat}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {activeSection === 'roadmap' ? (
        <div className="relative space-y-6 pl-4 md:pl-8">
          <div
            className="absolute bottom-0 left-[11px] top-2 w-px md:left-[19px]"
            style={{ background: 'var(--border)' }}
            aria-hidden
          />
          {(['month_1', 'month_2', 'month_3', 'month_6', 'month_12'] as const).map((m, idx) => {
            const rm = gr?.[m];
            if (!rm) return null;
            const colors = [theme.primary, theme.secondary, theme.accent, '#8B5CF6', '#F59E0B'];
            const col = colors[idx % colors.length];
            return (
              <div key={m} className="relative flex gap-4">
                <div
                  className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[11px] font-black text-white"
                  style={{ background: col }}
                >
                  {m.replace('month_', 'M')}
                </div>
                <div className="odc-card min-w-0 flex-1 !rounded-xl !p-4" style={{ transform: 'none' }}>
                  <p className="text-base font-bold" style={{ color: 'var(--text)' }}>
                    {String(rm.title)}
                  </p>
                  <p className="mt-1 text-sm italic" style={{ color: 'var(--muted)' }}>
                    {String(rm.focus)}
                  </p>
                  <ol className="mt-3 list-decimal space-y-1 pl-4 text-sm" style={{ color: 'var(--text)' }}>
                    {((rm.key_actions as string[]) ?? []).map((a) => (
                      <li key={a}>{a}</li>
                    ))}
                  </ol>
                  <p
                    className="mt-3 rounded-lg p-3 text-sm font-semibold"
                    style={{ background: 'rgba(16,185,129,0.12)', color: '#6EE7B7' }}
                  >
                    Expected: {String(rm.expected_results)}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {((rm.agents_to_activate as string[]) ?? []).map((ag) => (
                      <span
                        key={ag}
                        className="rounded-full px-2 py-0.5 text-[11px] font-bold"
                        style={{ background: 'rgba(16,185,129,0.18)', color: '#6EE7B7' }}
                      >
                        {ag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {activeSection === 'roi' ? (
        <div className="space-y-6">
          <div className="odc-card !p-6">
            <h2 className="text-lg font-bold" style={{ color: 'var(--text)' }}>
              💰 Expected Returns
            </h2>
            <div className="mt-4 grid gap-4 md:grid-cols-3">
              {[
                { label: 'Month 1', key: 'month_1_projection' },
                { label: 'Month 3', key: 'month_3_projection' },
                { label: 'Month 6', key: 'month_6_projection' },
              ].map(({ label, key }) => {
                const block = roi?.[key] as Record<string, unknown> | undefined;
                if (!block) return null;
                return (
                  <div key={key} className="odc-card !rounded-xl !p-4" style={{ transform: 'none' }}>
                    <p className="text-xs font-bold" style={{ color: 'var(--muted)' }}>
                      {label}
                    </p>
                    <p className="odc-metric mt-2 text-2xl" style={{ color: theme.primary }}>
                      {String(block.leads ?? '')}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>
                      Leads
                    </p>
                    <p className="mt-2 text-sm" style={{ color: 'var(--text)' }}>
                      Conversions: {String(block.conversions ?? '')}
                    </p>
                    <p className="mt-2 text-lg font-black" style={{ color: theme.primary }}>
                      {String(block.revenue_potential ?? '')}
                    </p>
                    {block.follower_growth != null ? (
                      <p className="mt-1 text-sm" style={{ color: '#22D3EE' }}>
                        Follower growth: {String(block.follower_growth)}
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="odc-card !p-6">
            <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>
              Investment vs returns (illustrative)
            </p>
            <div className="mt-4 flex items-end gap-4">
              <div className="flex-1">
                <p className="text-[11px]" style={{ color: 'var(--muted)' }}>
                  Monthly Investment
                </p>
                <div className="mt-2 h-24 overflow-hidden rounded-lg" style={{ background: 'var(--card2)' }}>
                  <div className="h-[35%] w-full" style={{ background: '#64748B' }} />
                </div>
                <p className="mt-2 text-xs" style={{ color: 'var(--muted)' }}>
                  {String(roi?.investment || 'Plan dependent')}
                </p>
              </div>
              <div className="flex-1">
                <p className="text-[11px]" style={{ color: 'var(--muted)' }}>
                  Month 3 revenue potential
                </p>
                <div className="mt-2 h-24 overflow-hidden rounded-lg" style={{ background: 'var(--card2)' }}>
                  <div className="h-[85%] w-full" style={{ background: theme.primary }} />
                </div>
                <p className="mt-2 text-xs font-bold" style={{ color: theme.primary }}>
                  {String((roi?.month_3_projection as Record<string, unknown> | undefined)?.revenue_potential || '')}
                </p>
              </div>
            </div>
          </div>

          <div className="odc-card !p-6">
            <p className="text-xs font-bold uppercase" style={{ color: 'var(--muted)' }}>
              Assumptions
            </p>
            <ul className="mt-2 space-y-1 text-sm italic" style={{ color: 'var(--muted)' }}>
              {((roi?.assumptions as string[]) ?? []).map((a) => (
                <li key={a}>• {a}</li>
              ))}
            </ul>
          </div>

          <div
            className="odc-card !p-6"
            style={{ borderColor: 'rgba(16,185,129,0.45)', background: 'rgba(16,185,129,0.06)' }}
          >
            <h3 className="text-lg font-black" style={{ color: 'var(--text)' }}>
              📦 Our Recommendation
            </h3>
            <p className="mt-2 text-2xl font-black" style={{ color: 'var(--text)' }}>
              {String(plan?.plan_name)}
            </p>
            <p className="mt-1 text-xl font-black" style={{ color: theme.primary }}>
              {String(plan?.monthly_cost)}
            </p>
            <p className="mt-2 text-sm" style={{ color: 'var(--muted)' }}>
              AI agents scale with your selected plan.
            </p>
            <p className="mt-3 text-sm" style={{ color: 'var(--text)' }}>
              {String(plan?.why_this_plan)}
            </p>
            <p className="mt-2 text-sm italic" style={{ color: 'var(--muted)' }}>
              {String(plan?.upgrade_trigger)}
            </p>
            <Link to="/pricing" className="odc-btn-primary mt-6 inline-flex justify-center">
              Get Started with {String(plan?.plan_name || 'this plan')}
            </Link>
          </div>

          <div className="rounded-xl border px-4 py-3 text-xs italic" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}>
            ⚠️ Note: These projections are based on market averages. Actual outcomes depend on execution quality.
          </div>
        </div>
      ) : null}
    </div>
  );
}
